import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { statusName } from '../lib/tenant';
import { Icons } from '../components/icons';
import { actionLabel, formatAuditSummary, formatRelativeTime } from '../lib/auditLabels';

function go(page: string) {
  window.dispatchEvent(new CustomEvent('nav', { detail: page }));
}

function num(v: unknown, fallback = '—') {
  if (v == null) return fallback;
  return typeof v === 'number' ? v.toLocaleString('zh-CN') : String(v);
}

function pct(v: unknown) {
  if (v == null || typeof v !== 'number') return '—';
  return v.toFixed(1) + '%';
}

const MODULES = [
  {
    title: '租户与商务',
    desc: '开户、套餐、支付与 Open API',
    items: [
      { key: 'tenants', label: '租户管理', desc: '开户、试用转正、暂停与注销', icon: <Icons.Users w={18} h={18} /> },
      { key: 'plans', label: '套餐方案', desc: '配额与套餐定价', icon: <Icons.Plan w={18} h={18} /> },
      { key: 'billing', label: '支付配置', desc: '支付宝等收款通道', icon: <Icons.Payment w={18} h={18} /> },
      { key: 'api-keys', label: 'Open API', desc: '密钥用量与调用趋势', icon: <Icons.Key w={18} h={18} /> },
    ],
  },
  {
    title: '内容与终端',
    desc: '行业模板、工位 MQTT 与 Broker 运维',
    items: [
      { key: 'templates', label: '行业模板', desc: '预置 SOP / 行业方案', icon: <Icons.Layers w={18} h={18} /> },
      { key: 'station-mqtt', label: '工位 MQTT', desc: '工位 App MQTT 连接参数', icon: <Icons.Monitor w={18} h={18} /> },
      { key: 'mosquitto-ops', label: 'MQTT 运维', desc: '用户、ACL、连接探测（可选）', icon: <Icons.CloudServer w={18} h={18} /> },
    ],
  },
  {
    title: '平台配置',
    desc: 'AI、存储与 OEM 品牌等全局能力',
    items: [
      { key: 'ai-settings', label: 'AI 服务', desc: '平台级模型与配额', icon: <Icons.Zap w={18} h={18} /> },
      { key: 'storage-settings', label: '对象存储', desc: '文档与附件存储后端', icon: <Icons.Layers w={18} h={18} /> },
      { key: 'branding-settings', label: 'OEM 品牌', desc: '系统名称与 LOGO 定制', icon: <Icons.Plan w={18} h={18} /> },
    ],
  },
  {
    title: '运维与合规',
    desc: '审计追溯与微服务健康',
    items: [
      { key: 'audit', label: '操作审计', desc: '跨租户操作记录', icon: <Icons.Shield w={18} h={18} /> },
      { key: 'health', label: '系统监控', desc: '微服务与基础设施探测', icon: <Icons.Monitor w={18} h={18} /> },
    ],
  },
];

export function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [recent, setRecent] = useState([]);
  const [plansCount, setPlansCount] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch('/api/v1/platform/analytics-overview'),
      apiFetch('/api/v1/platform/services/health'),
      apiFetch('/api/v1/platform/audit-logs?page=1&page_size=6'),
      apiFetch('/api/v1/plans'),
    ])
      .then(([a, h, logs, plans]) => {
        if (a.code === 0) setOverview(a.data || null);
        if (h.code === 0) setHealth(h.data || null);
        if (logs.code === 0) setRecent(logs.data?.items || []);
        if (plans.code === 0) setPlansCount((plans.data || []).length);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const funnel = overview?.trial_funnel || {};
  const statusRows = overview?.tenants_by_status || [];
  const signupTrend = overview?.tenant_signup_trend || [];
  const auditActions = overview?.audit_actions_7d || [];
  const maxSignup = Math.max(1, ...signupTrend.map((x) => x.count || 0));
  const maxAudit = Math.max(1, ...auditActions.map((x) => x.count || 0));

  const svcUp = health?.summary?.up ?? 0;
  const svcDown = health?.summary?.down ?? 0;
  const svcTotal = health?.summary?.total ?? 0;
  const infraDown = health?.infra_summary?.down ?? 0;
  const gwVer = health?.gateway_version || '—';
  const healthOk = svcDown === 0 && infraDown === 0 && svcTotal > 0;

  const today = new Date().toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  return (
    <div className="wb">
      <div className="wb-hero">
        <div className="wb-hero-main">
          <div className="wb-hero-kicker">经纬循道信息 · 数据中台</div>
          <h1 className="wb-hero-title">工作台</h1>
          <p className="wb-hero-sub">{today} · 租户运营、终端配置与平台运维一览</p>
        </div>
        <div className="wb-hero-side">
          <button
            type="button"
            className={'wb-health-pill ' + (healthOk ? 'ok' : svcTotal === 0 ? 'muted' : 'bad')}
            onClick={() => go('health')}
            title="打开系统监控"
          >
            <span className="wb-health-dot" />
            <span>
              {svcTotal === 0
                ? '健康检查加载中'
                : healthOk
                  ? `服务正常 ${svcUp}/${svcTotal}`
                  : `异常 ${svcDown} 个服务` + (infraDown > 0 ? ` · 基础设施 ${infraDown}` : '')}
            </span>
            <span className="wb-health-ver">网关 v{gwVer}</span>
          </button>
          <button type="button" className="btn btn-outline btn-xs" onClick={load} disabled={loading}>
            <Icons.Refresh w={12} h={12} /> 刷新
          </button>
        </div>
      </div>

      <div className="wb-kpi-grid">
        <button type="button" className="wb-kpi" onClick={() => go('tenants')}>
          <div className="wb-kpi-label">注册租户</div>
          <div className="wb-kpi-value">{loading && !overview ? '…' : num(overview?.total_tenants)}</div>
          <div className="wb-kpi-hint">近 30 天新开户 {num(overview?.new_tenants_30d, '0')}</div>
        </button>
        <button type="button" className="wb-kpi" onClick={() => go('tenants')}>
          <div className="wb-kpi-label">正式租户</div>
          <div className="wb-kpi-value">{num(overview?.active_tenants)}</div>
          <div className="wb-kpi-hint">已激活 / 付费</div>
        </button>
        <button type="button" className="wb-kpi accent" onClick={() => go('tenants')}>
          <div className="wb-kpi-label">试用中</div>
          <div className="wb-kpi-value">{num(overview?.trial_tenants)}</div>
          <div className="wb-kpi-hint">待转化 {num(funnel.converted_30d, '0')}（30 天）</div>
        </button>
        <button type="button" className="wb-kpi" onClick={() => go('api-keys')}>
          <div className="wb-kpi-label">API 调用（7 天）</div>
          <div className="wb-kpi-value">{num(overview?.api_calls_7d)}</div>
          <div className="wb-kpi-hint">套餐方案 {plansCount != null ? plansCount : '—'}</div>
        </button>
      </div>

      <div className="wb-metric-row">
        <div className="wb-metric">
          <span className="wb-metric-label">SOP 总数</span>
          <strong>{num(overview?.total_sops)}</strong>
        </div>
        <div className="wb-metric">
          <span className="wb-metric-label">执行记录</span>
          <strong>{num(overview?.total_executions)}</strong>
        </div>
        <div className="wb-metric">
          <span className="wb-metric-label">审批流程</span>
          <strong>{num(overview?.total_workflows)}</strong>
        </div>
        <div className="wb-metric clickable" onClick={() => go('health')}>
          <span className="wb-metric-label">微服务</span>
          <strong style={{ color: svcDown > 0 ? '#dc2626' : undefined }}>
            {svcTotal ? `${svcUp}/${svcTotal}` : '—'}
          </strong>
        </div>
      </div>

      <div className="wb-grid-2">
        <div className="card wb-card">
          <div className="wb-card-head">
            <div className="card-title" style={{ margin: 0 }}>试用转化</div>
            <button type="button" className="wb-link" onClick={() => go('tenants')}>管理租户</button>
          </div>
          <div className="wb-funnel">
            <div className="wb-funnel-row">
              <span>试用中</span>
              <strong>{num(funnel.trial)}</strong>
            </div>
            <div className="wb-funnel-bar">
              <div
                className="wb-funnel-fill trial"
                style={{
                  width:
                    (funnel.trial || 0) + (funnel.active || 0) > 0
                      ? ((funnel.trial || 0) / ((funnel.trial || 0) + (funnel.active || 0))) * 100 + '%'
                      : '0%',
                }}
              />
            </div>
            <div className="wb-funnel-row">
              <span>正式租户</span>
              <strong>{num(funnel.active)}</strong>
            </div>
            <div className="wb-funnel-bar">
              <div
                className="wb-funnel-fill active"
                style={{
                  width:
                    (funnel.trial || 0) + (funnel.active || 0) > 0
                      ? ((funnel.active || 0) / ((funnel.trial || 0) + (funnel.active || 0))) * 100 + '%'
                      : '0%',
                }}
              />
            </div>
            <div className="wb-funnel-stats">
              <div>
                <div className="wb-funnel-stat-val">{num(funnel.converted_30d, '0')}</div>
                <div className="wb-funnel-stat-lbl">近 30 天转化</div>
              </div>
              <div>
                <div className="wb-funnel-stat-val">{pct(funnel.conversion_rate_pct)}</div>
                <div className="wb-funnel-stat-lbl">正式占比</div>
              </div>
            </div>
          </div>
          <div className="wb-status-list">
            <div className="wb-section-label">租户状态</div>
            {statusRows.length === 0 ? (
              <p className="wb-empty">暂无数据</p>
            ) : (
              statusRows.map((s) => (
                <div key={s.status} className="wb-status-row">
                  <span>{statusName(s.status)}</span>
                  <strong>{s.count}</strong>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card wb-card">
          <div className="wb-card-head">
            <div className="card-title" style={{ margin: 0 }}>近 30 天开户趋势</div>
            <span className="wb-muted">{signupTrend.reduce((n, x) => n + (x.count || 0), 0)} 户</span>
          </div>
          {signupTrend.length === 0 ? (
            <p className="wb-empty">暂无新开户</p>
          ) : (
            <div className="wb-chart">
              {signupTrend.map((pt) => (
                <div key={pt.day} className="wb-chart-col" title={`${pt.day}：${pt.count} 户`}>
                  <div
                    className="wb-chart-bar"
                    style={{ height: Math.max(4, ((pt.count || 0) / maxSignup) * 100) + '%' }}
                  />
                  <div className="wb-chart-lbl">{pt.day.slice(8)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="wb-grid-2">
        <div className="card wb-card">
          <div className="wb-card-head">
            <div className="card-title" style={{ margin: 0 }}>近 7 天审计热点</div>
            <button type="button" className="wb-link" onClick={() => go('audit')}>全部审计</button>
          </div>
          {auditActions.length === 0 ? (
            <p className="wb-empty">暂无审计记录</p>
          ) : (
            <div className="wb-audit-list">
              {auditActions.map((a) => (
                <div key={a.action} className="wb-audit-row" title={a.action}>
                  <div className="wb-audit-meta">
                    <span>{actionLabel(a.action)}</span>
                    <strong>{a.count}</strong>
                  </div>
                  <div className="wb-audit-track">
                    <div className="wb-audit-fill" style={{ width: ((a.count || 0) / maxAudit) * 100 + '%' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card wb-card">
          <div className="wb-card-head">
            <div className="card-title" style={{ margin: 0 }}>最近平台动态</div>
            <button type="button" className="wb-link" onClick={() => go('audit')}>查看更多</button>
          </div>
          {recent.length === 0 ? (
            <p className="wb-empty">暂无动态</p>
          ) : (
            <div className="wb-activity">
              {recent.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  className="wb-activity-item"
                  onClick={() => go('audit')}
                  title={formatAuditSummary(it)}
                >
                  <div className="wb-activity-top">
                    <span className="wb-activity-title">{actionLabel(it.action)}</span>
                    <span className="wb-activity-time">{formatRelativeTime(it.created_at)}</span>
                  </div>
                  <div className="wb-activity-msg">{formatAuditSummary(it, { compact: true })}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="wb-modules">
        <div className="wb-modules-head">
          <h2>功能入口</h2>
          <p>按现有菜单能力分组，一键进入对应模块</p>
        </div>
        {MODULES.map((group) => (
          <div key={group.title} className="wb-module-group">
            <div className="wb-module-group-head">
              <strong>{group.title}</strong>
              <span>{group.desc}</span>
            </div>
            <div className="wb-module-grid">
              {group.items.map((item) => (
                <button key={item.key} type="button" className="wb-module-card" onClick={() => go(item.key)}>
                  <span className="wb-module-icon">{item.icon}</span>
                  <span className="wb-module-text">
                    <span className="wb-module-label">{item.label}</span>
                    <span className="wb-module-desc">{item.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
